import dayjs from 'dayjs';

import { convertTemperature } from '../../../lib/convert';
import { resolveActiveThreshold } from './workingHours';

export const CONSTANTS = {
    DATA_THRESHOLD: 432,
    SAMPLING_RATE: 6,
    UPDATE_INTERVAL: 60000,
    RECENT_DATA_MINUTES: 10,
    DEFAULT_TIME_RANGE: 1,
    COLORS: {
        NORMAL: '#3F8EE8',
        ABNORMAL: '#FF3838',
        BACKGROUND: '#E6E6FF',
        CARD_NORMAL: '#BFE4FF',
        CARD_ABNORMAL: '#FFDFDF',
    },
};

export const dataUtils = {
    sampleData: (data: any[], samplingRate = CONSTANTS.SAMPLING_RATE) =>
        data.filter((_item: any, index: number) => index % samplingRate === 0),

    // Whether a single reading was within limits *at the moment it was taken*. A
    // device on working hours has two threshold sets, so a 3am reading and a 3pm
    // reading are judged against different bounds. Rows carrying no raw timestamp
    // (older shapes) fall back to the set in force now.
    isReadingNormal: (record: any, item: any) => {
        const { upperLimit, lowerLimit } = resolveActiveThreshold(
            record,
            item?.timestamp ? new Date(item.timestamp) : new Date()
        );
        return dataUtils.isTemperatureNormal(item?.temperature, upperLimit, lowerLimit);
    },

    processTemperatureData: (detail: any[], record: any) => {
        const isLargeDataset = detail.length > CONSTANTS.DATA_THRESHOLD;

        // Gaps (no reading) belong to neither series — plotting them as abnormal
        // would paint red dots where there is simply no data.
        const measured = detail.filter(
            (item: any) => item?.temperature !== null && item?.temperature !== undefined && item?.temperature !== ''
        );

        const normalData = measured.filter((item: any) => dataUtils.isReadingNormal(record, item));
        const processedNormalData = isLargeDataset ? dataUtils.sampleData(normalData) : normalData;

        const abnormalData = measured.filter((item: any) => !dataUtils.isReadingNormal(record, item));

        return {
            normalData: processedNormalData,
            abnormalData,
            allData: detail,
        };
    },

    extractPlotData: (data: any[]) => ({
        x: data.map((item: any) => item.time),
        y: data.map((item: any) => item.temperature),
    }),

    formatTimeForAPI: (time: dayjs.ConfigType) => {
        const now = dayjs();
        const timeOffset = now.utcOffset();
        return dayjs(time).subtract(timeOffset, 'm').format('YYYY-MM-DDTHH:mm:ss+00:00');
    },

    isTemperatureNormal: (temperature: any, upperLimit: number, lowerLimit: number) =>
        temperature !== null && temperature !== undefined && temperature >= lowerLimit && temperature <= upperLimit,
};

// Quick-range shortcuts shown in the device detail dialog. Each entry maps a
// label to a relative span ending "now"; `Custom` is intentionally omitted for
// now (manual editing is done via the From/To pickers).
export const QUICK_RANGES = [
    { key: '6H', amount: 6, unit: 'hour' },
    { key: '12H', amount: 12, unit: 'hour' },
    { key: '24H', amount: 24, unit: 'hour' },
    { key: '3D', amount: 3, unit: 'day' },
    { key: '7D', amount: 7, unit: 'day' },
];

export const DEFAULT_QUICK_RANGE = { key: '24H', amount: 24, unit: 'hour' };

export const DEFAULT_QUICK_RANGE_KEY = DEFAULT_QUICK_RANGE.key;

// How close to "now" a window's end must be to count as showing the latest data.
// Presets resolve to a fixed `end` that goes stale as the dialog sits open, so an
// exact comparison would report a freshly-opened 24H window as already panned.
export const AT_LATEST_TOLERANCE_MS = 60_000;

/**
 * Parse a value read off a chart axis into a dayjs, or null if it isn't a real
 * datetime.
 *
 * A date axis reports date strings, but a Plotly axis that has fallen back to
 * numeric (which happens when the window holds no points) reports bare numbers
 * like -1 and 6. Those parse into far-past datetimes rather than failing, so a
 * plain isValid() check would happily commit them to the pickers.
 */
const parseAxisTime = (value: any) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number') return null;
    if (typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value.trim())) return null;

    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
};

export const timeUtils = {
    getDefaultTimeRange: () => ({
        start: dayjs().subtract(CONSTANTS.DEFAULT_TIME_RANGE, 'week'),
        end: dayjs(),
    }),

    getRecentTimeRange: (endTime: dayjs.ConfigType) => ({
        start: dayjs(endTime).subtract(CONSTANTS.RECENT_DATA_MINUTES, 'm'),
        end: dayjs(endTime),
    }),

    // Resolve a quick-range span into a concrete [start, end] window ending now.
    getQuickRange: (amount: number, unit: any) => ({
        start: dayjs().subtract(amount, unit),
        end: dayjs(),
    }),

    // Normalise a window the user dragged the chart to.
    //
    // The chart can be panned into the future, which would ask the API for a
    // range that cannot contain data, so anything ending past `now` is slid back
    // until it ends exactly at `now`. The width is preserved rather than
    // truncated: a drag moves the window, it never resizes it.
    //
    // Returns null for a window that isn't usable at all. `now` is injectable to
    // keep this testable.
    clampWindowToNow: (start: any, end: any, now: dayjs.ConfigType = dayjs()) => {
        const from = parseAxisTime(start);
        const to = parseAxisTime(end);
        if (!from || !to) return null;

        const width = to.diff(from);
        if (!(width > 0)) return null;

        const reference = dayjs(now);
        const cappedEnd = to.isAfter(reference) ? reference : to;

        return {
            start: cappedEnd.subtract(width, 'millisecond'),
            end: cappedEnd,
            // A window resolved from a preset is already seconds stale by the
            // time anyone drags, so "within tolerance" counts as still live.
            anchoredToNow: reference.diff(cappedEnd) <= AT_LATEST_TOLERANCE_MS,
        };
    },

    formatDisplayTime: (time: dayjs.ConfigType) => (time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : null),
};

export const createData = (record: any, index: number, tempUnit: string) => {
    const now = dayjs();
    const timeZone = dayjs(now).format('UTCZ');

    // A device may be only partially configured (e.g. registered but never
    // assigned a threshold or first reading). Default the optional pieces so a
    // single malformed record can never throw and blank the whole grid.
    // Copied before the limits are converted below: the record may be a cached API
    // object that gets mapped again (e.g. on a unit change), and converting it in
    // place would convert the limits twice.
    const threshold = { ...(record.threshold ?? {}) };
    const upperLimit = convertTemperature(threshold.upper_limit, tempUnit);
    const lowerLimit = convertTemperature(threshold.lower_limit, tempUnit);

    threshold.upper_limit = upperLimit;
    threshold.lower_limit = lowerLimit;

    const latest = record.latest || record;
    const gatewayId = latest.gateway_id ? latest.gateway_id.split('-').pop() : null;

    const lastItem = {
        temperature: convertTemperature(latest.temperature ?? record.temperature, tempUnit),
        time: timeUtils.formatDisplayTime(latest.create_time ?? latest.time ?? record.time),
        gatewayId,
    };

    const latestTime = latest.time ?? record.time;
    const intervalSeconds = Number(record.interval);
    const connected = latestTime
        ? (Number.isFinite(intervalSeconds) && dayjs(now).diff(dayjs(latestTime), 'seconds') <= intervalSeconds * 2) ||
          dayjs(now).diff(dayjs(latestTime), 'minutes') < 30
        : false;

    // Working-hours set (null when unconfigured), normalised the same way as the
    // top-level set so both are in the display unit before anything compares them.
    const workingHours = threshold.working_hours
        ? {
              enabled: !!threshold.working_hours.enabled,
              days: threshold.working_hours.days,
              startTime: threshold.working_hours.start_time,
              endTime: threshold.working_hours.end_time,
              timezone: threshold.working_hours.timezone,
              upperLimit: convertTemperature(threshold.working_hours.upper_limit, tempUnit),
              lowerLimit: convertTemperature(threshold.working_hours.lower_limit, tempUnit),
              timeLimit: threshold.working_hours.time_limit,
          }
        : null;

    // Which set is in force right now. The live card and its status colour follow
    // this; the top-level limits stay the non-working set so the config form can
    // keep editing the two sets independently.
    const active = resolveActiveThreshold(
        { upperLimit, lowerLimit, timeLimit: threshold.time_limit, workingHours },
        now.toDate()
    );

    const normal = dataUtils.isTemperatureNormal(lastItem.temperature, active.upperLimit, active.lowerLimit);

    return {
        num: index + 1,
        order: record.order ?? null,
        deviceId: record.device_id,
        name: record.name,
        // Owning company — vendor accounts see devices across companies, and a device
        // can only join a group in its own company (see manage-devices filtering).
        companyId: record.company_id ?? null,
        // Group memberships this device belongs to (absent/empty when ungrouped).
        groupIds: record.group_ids || [],
        lastItem,
        connected,
        normal,
        timeZone,
        lowerLimit,
        upperLimit,
        timeLimit: threshold.time_limit,
        thresholdType: threshold.type,
        // Offline-notification threshold (seconds) — how long a device may be silent
        // before a disconnect alert fires. See swagger RealtimeThreshold.miss_time_limit.
        missTimeLimit: threshold.miss_time_limit,
        // Working-hours threshold set (null when the device has none configured). The
        // top-level upper/lower/timeLimit above are the non-working (default) set.
        workingHours,
        // The set in force at render time, for anything showing the live reading.
        activeUpperLimit: active.upperLimit,
        activeLowerLimit: active.lowerLimit,
        activeTimeLimit: active.timeLimit,
        inWorkingHours: active.isWorkingHours,
        interval: record.interval,
        threshold,
        metaData: record.meta_data,
        modelInfo: record.model_info,
        email: record.meta_data?.email,
        emailNotification: record.email_notification ?? false,
        appNotification: record.app_notification ?? false,
        lineNotification: record.line_notification ?? false,
        notifyEmails: record.notify_emails || [],
    };
};
